
import React from 'react';
import { Link } from "react-router-dom";
import { Shield, RefreshCw, MapPin, Building2, Phone, Mail } from "lucide-react";
import OptimizedImage from "@/components/shared/OptimizedImage";
import { CONTACT } from "@/constants/config";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="bg-muted/50 pt-16 pb-8 relative">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
          <div className="space-y-4">
            <Link to="/" className="flex items-center text-2xl font-bold theme-primary-text gap-1">
              <OptimizedImage
                src="/lovable-uploads/7b314dd5-c766-4046-8e2f-02a35e9e67c2.png"
                alt="Propico Logo"
                className="h-8 w-12"
                width={48}
                height={32}
              />
              Propico
            </Link>
            <p className="text-foreground/70 max-w-xs">
              Biến đổi trải nghiệm cho thuê cho chủ sở hữu bất động sản trên toàn Việt Nam.
            </p>
            <div className="space-y-2 text-sm text-foreground/70">
              <p className="flex items-start gap-2">
                <Building2 size={14} className="mt-0.5 shrink-0 theme-primary-text" />
                <span>Công ty TNHH Propico Việt Nam — MST/ĐKKD: 0318428591</span>
              </p>
              <p className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 shrink-0 theme-primary-text" />
                <span>Tầng 8, Tòa nhà Bitexco Nam Long, 63A Võ Văn Tần, Q.3, TP.HCM</span>
              </p>
              <p className="flex items-center gap-2">
                <Phone size={14} className="theme-primary-text" />
                {/* TODO: Replace with real phone number from config once verified */}
                <a href={`tel:${CONTACT.HOTLINE}`} className="hover:theme-primary-text transition-colors">{CONTACT.HOTLINE_DISPLAY} (Hotline/Zalo)</a>
              </p>
              <p className="flex items-center gap-2">
                <Mail size={14} className="theme-primary-text" />
                <a href={`mailto:${CONTACT.EMAIL}`} className="hover:theme-primary-text transition-colors">{CONTACT.EMAIL}</a>
              </p>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4 theme-primary-text">Liên Kết Hữu Ích</h4>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/about" 
                  className="text-foreground/70 hover:theme-primary-text hover:translate-x-1 transition-all inline-block"
                  onClick={scrollToTop}
                >
                  Về Chúng Tôi
                </Link>
              </li>
              <FooterLink href="#guarantee" onClick={() => scrollToSection('triple-guarantee')}>Cam Kết Của Chúng Tôi</FooterLink>
              <FooterLink href="#how-it-works" onClick={() => scrollToSection('how-it-works')}>Quy Trình Hoạt Động</FooterLink>
              <FooterLink href="#packages" onClick={() => scrollToSection('packages')}>Bảng Giá</FooterLink>
              <FooterLink href="#faq" onClick={() => scrollToSection('faq')}>Câu Hỏi Thường Gặp</FooterLink>
              <li>
                <Link 
                  to="/terms-of-use" 
                  className="text-foreground/70 hover:theme-primary-text hover:translate-x-1 transition-all inline-block"
                  onClick={scrollToTop}
                >
                  Điều Khoản Sử Dụng
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact-us" 
                  className="text-foreground/70 hover:theme-primary-text hover:translate-x-1 transition-all inline-block"
                  onClick={scrollToTop}
                >
                  Liên Hệ
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-foreground/70">
          <div className="mb-4 md:mb-0">
            © {currentYear} Propico. Bảo lưu mọi quyền.
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            <Link 
              to="/terms-of-use" 
              className="hover:theme-primary-text transition-colors"
              onClick={scrollToTop}
            >
              Điều Khoản Sử Dụng
            </Link>
            <Link 
              to="/privacy-policy" 
              className="hover:theme-primary-text transition-colors flex items-center gap-1"
              onClick={scrollToTop}
            >
              <Shield size={14} />
              Chính Sách Bảo Mật
            </Link>
            <Link 
              to="/refund-policy" 
              className="hover:theme-primary-text transition-colors flex items-center gap-1"
              onClick={scrollToTop}
            >
              <RefreshCw size={14} />
              Chính Sách Hoàn Tiền
            </Link>
            <Link 
              to="/contact-us" 
              className="hover:theme-primary-text transition-colors"
              onClick={scrollToTop}
            >
              Liên Hệ
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};


const FooterLink = ({ href, onClick, children }: { 
  href: string; 
  onClick?: () => void;
  children: React.ReactNode 
}) => {
  return (
    <li>
      <a 
        href={href} 
        className="text-foreground/70 hover:theme-primary-text hover:translate-x-1 transition-all inline-block"
        onClick={(e) => {
          e.preventDefault();
          if (onClick) onClick();
        }}
      >
        {children}
      </a>
    </li>
  );
};

export default Footer;
